import { useMutation } from '@apollo/client';
import { GridApi } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { MutableRefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctDemandResponsibleUsersInput } from '@/generated/gql/graphql';
import { NegotiationDemandDataGridRow } from '~/common/types';
import { PriorityCellContext, UserSelectCellContext } from '~/components/DataGridCustom';
import { isString } from '~/util/string';

import { NEGOTIATION_DEMAND_UPDATE_PRIORITY_MUTATION } from './NegotiationDemandUpdatePriority.gql';
import { NEGOTIATION_DEMAND_UPDATE_RESPONSIBLE_MUTATION } from './NegotiationDemandUpdateResponsible.gql';

type DemandGridContext = UserSelectCellContext & PriorityCellContext;

interface UseDemandDataGridProps {
  apiRef: MutableRefObject<GridApi>;
}

export const useDemandDataGrid = ({ apiRef }: UseDemandDataGridProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'DemandDataGrid.error' });
  const { enqueueErrorSnackbar } = useCustomSnackbar();

  const [updateUser] = useMutation(NEGOTIATION_DEMAND_UPDATE_RESPONSIBLE_MUTATION, { errorPolicy: 'all' });
  const [updatePriority] = useMutation(NEGOTIATION_DEMAND_UPDATE_PRIORITY_MUTATION, { errorPolicy: 'all' });

  const contextValue: DemandGridContext = useMemo(
    () => ({
      onUserChange: async (rowId, user) => {
        const row = apiRef.current.getRow<NegotiationDemandDataGridRow>(rowId);
        if (row) {
          let userId: string | null = null;
          const responsibleIds: string[] = [];
          if (user) {
            if (isString(user)) {
              userId = user;
              responsibleIds.push(userId);
            } else {
              userId = user.primarySourceReference;
              responsibleIds.push(userId);
            }
          }
          // update datagrid-row cache to reflect change immediately
          apiRef.current.updateRows([{ ...row, responsibleIds }]);

          const updateInput: TsrctDemandResponsibleUsersInput = {
            responsibleUserIds: userId ? [userId] : [],
          };
          // mutation to update entry in the db
          const { data, errors } = await updateUser({
            variables: {
              id: row.id,
              data: updateInput,
            },
            // update the apollo cache to reflect the change if the entity from cache is used
            update: (cache, result) => {
              cache.modify({
                id: cache.identify({
                  __typename: 'TsrctDemandListItem',
                  id: row.id,
                }),
                fields: {
                  responsibleIds: () => {
                    return result.data?.tesseract.demand.responsibleUsers.update.responsibleUserIds;
                  },
                },
              });
            },
          });
          const updatedUserId = data?.tesseract.demand.responsibleUsers.update.responsibleUserIds?.[0] || null;
          if (errors || updatedUserId !== userId) {
            // rollback the changes in the grid cache
            apiRef.current.updateRows([row]);
            enqueueErrorSnackbar(t('updateUser'));
          }
        }
      },
      onPriorityChange: async (rowId, priority) => {
        const row = apiRef.current.getRow<NegotiationDemandDataGridRow>(rowId);
        if (row) {
          // update datagrid-row cache to reflect change immediately
          apiRef.current.updateRows([{ ...row, priority }]);

          // mutation to update entry in the db
          const { data, errors } = await updatePriority({
            variables: {
              id: row.id,
              data: { priority },
            },

            // update the apollo cache to reflect the change if the entity from cache is used
            update: (cache, result) => {
              cache.modify({
                id: cache.identify({
                  __typename: 'TsrctDemandListItem',
                  id: row.id,
                }),
                fields: {
                  priority: () => {
                    return result.data?.tesseract.demand?.priority.update.priority;
                  },
                },
              });
            },
          });
          if (errors || data?.tesseract.demand?.priority.update.priority !== priority) {
            // rollback the changes in the grid cache
            apiRef.current.updateRows([row]);
            enqueueErrorSnackbar(t('updatePriority'));
          }
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  return {
    contextValue,
  };
};
